/* T&F Product Page interactions
 * - Image gallery: arrow nav + thumbnail click + variant-image sync
 * - Variant picker: color swatches + size buttons → updates URL & price
 * - Carousel: horizontal scroll with arrow nav for "You may also like"
 */
(function () {
  'use strict';

  /* ---------------- Gallery ---------------- */
  class TandfGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.tandf-product__gallery-viewport');
      this.track = this.querySelector('.tandf-product__gallery-track');
      this.slides = Array.from(this.querySelectorAll('.tandf-product__gallery-slide'));
      this.thumbs = Array.from(this.querySelectorAll('.tandf-product__gallery-thumb'));
      this.prevBtn = this.querySelector('.tandf-product__gallery-arrow--prev');
      this.nextBtn = this.querySelector('.tandf-product__gallery-arrow--next');
      this.index = 0;

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.go(i));
      });

      this.update();
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.go(this.index - 1);
        if (e.key === 'ArrowRight') this.go(this.index + 1);
      });
    }

    go(i) {
      const max = this.slides.length - 1;
      this.index = Math.max(0, Math.min(max, i));
      this.update();
    }

    goToMediaId(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i >= 0) this.go(i);
    }

    update() {
      if (this.track) {
        this.track.style.transform = `translateX(-${this.index * 100}%)`;
      }
      this.thumbs.forEach((thumb, i) => {
        thumb.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
      if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', this.index === 0);
      if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', this.index === this.slides.length - 1);
    }
  }

  /* ---------------- Variant picker ---------------- */
  class TandfVariantPicker extends HTMLElement {
    connectedCallback() {
      this.productJsonScript = this.querySelector('[data-product-json]');
      if (!this.productJsonScript) return;
      try {
        this.product = JSON.parse(this.productJsonScript.textContent);
      } catch (e) {
        return;
      }
      this.priceEl = document.querySelector('[data-tandf-price]');
      this.atcEl = document.querySelector('[data-tandf-atc]');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.galleryEl = document.querySelector('tandf-gallery');
      this.inputs = Array.from(this.querySelectorAll('input[type="radio"][data-option-index]'));
      this.inputs.forEach((input) =>
        input.addEventListener('change', (e) => this.onChange(e.target))
      );
      this.onChange();
    }

    onChange(changedInput) {
      const selected = {};
      this.inputs.forEach((input) => {
        if (input.checked) selected[input.dataset.optionIndex] = input.value;
      });
      const variant = this.product.variants.find((v) =>
        Object.keys(selected).every((k) => v.options[Number(k)] === selected[k])
      );
      if (!variant) return;

      // Update hidden id
      if (this.variantInput) this.variantInput.value = variant.id;

      // Update URL ?variant=
      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      // Update price
      if (this.priceEl) {
        //this.priceEl.textContent = this.formatMoney(variant.price);
      }

      // Update ATC state
      if (this.atcEl) {
        if (variant.available) {
          this.atcEl.removeAttribute('disabled');
          this.atcEl.textContent = (this.atcEl.dataset.labelAvailable || 'Add to Cart').toUpperCase();
        } else {
          this.atcEl.setAttribute('disabled', '');
          this.atcEl.textContent = (this.atcEl.dataset.labelSoldOut || 'Sold out').toUpperCase();
        }
      }

      // Sync gallery to variant featured image
      if (this.galleryEl && typeof this.galleryEl.goToMediaId === 'function') {
        let mediaId = variant.featured_media && variant.featured_media.id;

        // If a color was just changed and that exact variant has no media,
        // fall back to *any* variant of that color that does.
        if (!mediaId && changedInput && changedInput.dataset.optionKind === 'color') {
          const colorIndex = Number(changedInput.dataset.optionIndex);
          const colorValue = changedInput.value;
          const fallback = this.product.variants.find(
            (v) => v.options[colorIndex] === colorValue && v.featured_media
          );
          if (fallback) mediaId = fallback.featured_media.id;
        }

        if (mediaId) this.galleryEl.goToMediaId(mediaId);
      }
    }

    formatMoney(cents) {
      const fmt = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      const parts = amount.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const formatted = parts.length === 2 ? `${whole}.${parts[1]}` : whole;
      return fmt.replace(/\{\{\s*amount(?:_no_decimals)?\s*\}\}/g, formatted);
    }
  }

  /* ---------------- Product carousel ---------------- */
  class TandfProductCarousel extends HTMLElement {
    connectedCallback() {
      this.maybeLoadRecommendations();
      this.initRow();
    }

    maybeLoadRecommendations() {
      const url = this.dataset.recommendationsUrl;
      const productId = this.dataset.productId;
      const sectionId = this.dataset.sectionId;
      if (!url || !productId || !sectionId) return;

      // When the server already filled the carousel with real products
      // (no placeholder SVGs present), skip the extra fetch.
      if (!this.querySelector('.tandf-product-card__media .placeholder')) return;

      const target = `${url}&product_id=${productId}&section_id=${sectionId}`;
      fetch(target)
        .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
        .then((text) => {
          const tmp = document.createElement('div');
          tmp.innerHTML = text;
          const fresh = tmp.querySelector('tandf-product-carousel');
          if (!fresh || !fresh.innerHTML.trim()) return;
          this.innerHTML = fresh.innerHTML;
          this.initRow();
        })
        .catch((err) => console.warn('[T&F carousel] recommendations failed', err));
    }

    disconnectedCallback() {
      this.teardownRow();
    }

    teardownRow() {
      if (this._abort) {
        this._abort.abort();
        this._abort = null;
      }
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    }

    initRow() {
      // Re-runs after recommendations swap innerHTML — tear down stale
      // listeners/observers/timers before re-wiring against fresh nodes.
      this.teardownRow();

      this.row = this.querySelector('.tandf-product-carousel__cards');
      if (!this.row) return;
      this.prevBtn = this.querySelector('.tandf-product-carousel__nav--prev');
      this.nextBtn = this.querySelector('.tandf-product-carousel__nav--next');

      const cards = this.row.querySelectorAll('.tandf-product-card');
      if (cards.length < 2) return;

      this._abort = new AbortController();
      const signal = this._abort.signal;

      const step = () => cards[1].offsetLeft - cards[0].offsetLeft;
      const isAtEnd = () => this.row.scrollLeft + this.row.clientWidth >= this.row.scrollWidth - 2;
      const isAtStart = () => this.row.scrollLeft <= 2;

      const scrollNext = () => {
        if (isAtEnd()) this.row.scrollTo({ left: 0, behavior: 'smooth' });
        else this.row.scrollBy({ left: step(), behavior: 'smooth' });
      };
      const scrollPrev = () => {
        if (isAtStart()) this.row.scrollTo({ left: this.row.scrollWidth, behavior: 'smooth' });
        else this.row.scrollBy({ left: -step(), behavior: 'smooth' });
      };

      if (this.prevBtn) this.prevBtn.addEventListener('click', scrollPrev, { signal });
      if (this.nextBtn) this.nextBtn.addEventListener('click', scrollNext, { signal });

      const mobileMQ = window.matchMedia('(max-width: 989px)');
      const explicit = this.dataset.autoplayMs;
      const autoplayMs = explicit != null ? parseInt(explicit, 10) : (mobileMQ.matches ? 10000 : 0);
      if (autoplayMs <= 0) return;

      let paused = false;
      const start = () => {
        if (this._timer || paused) return;
        this._timer = setInterval(scrollNext, autoplayMs);
      };
      const stop = () => {
        if (!this._timer) return;
        clearInterval(this._timer);
        this._timer = null;
      };

      this.row.addEventListener('pointerdown', () => { paused = true; stop(); }, { signal });
      this.row.addEventListener('pointerup', () => { paused = false; start(); }, { signal });
      this.row.addEventListener('pointercancel', () => { paused = false; start(); }, { signal });
      this.addEventListener('focusin', stop, { signal });
      this.addEventListener('focusout', start, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop(); else start();
      }, { signal });

      this._io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) start(); else stop();
      }, { threshold: 0.25 });
      this._io.observe(this);
    }
  }

  /* ---------------- Add to cart ---------------- */
  class TandfBuyForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      e.preventDefault();
      const formData = new FormData(this.form);
      const button = this.form.querySelector('[type="submit"]');
      const originalLabel = button ? button.textContent : '';
      if (button) {
        button.setAttribute('disabled', '');
        button.textContent = 'ADDING…';
      }
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' }
        });
        if (!res.ok) throw new Error(await res.text());
        document.dispatchEvent(new CustomEvent('cart:item-added'));
        if (button) button.textContent = 'ADDED ✓';
        setTimeout(() => {
          if (button) {
            button.removeAttribute('disabled');
            button.textContent = originalLabel;
          }
        }, 1500);
      } catch (err) {
        console.error('[T&F] add to cart failed', err);
        if (button) {
          button.removeAttribute('disabled');
          button.textContent = originalLabel;
        }
      }
    }
  }

  /* ---------------- Size chart modal ---------------- */
  function initSizeChartModal(root) {
    const modal = root.querySelector('[data-tandf-size-chart-modal]');
    if (!modal) return;
    const opens = root.querySelectorAll('[data-tandf-size-chart-open]');
    const closeBtn = modal.querySelector('[data-tandf-size-chart-close]');
    let lastFocus = null;

    // The modal renders inside the variant-picker (position:sticky), which
    // creates a stacking context that traps fixed positioning — gallery
    // arrows end up painted on top. Re-parent to <body> so z-index 1200
    // actually wins.
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    const open = () => {
      lastFocus = document.activeElement;
      modal.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      document.body.classList.add('tandf-size-chart-open');
      const focusable = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable) focusable.focus();
    };
    const close = () => {
      modal.removeAttribute('open');
      document.documentElement.style.overflow = '';
      document.body.classList.remove('tandf-size-chart-open');
      if (lastFocus) lastFocus.focus();
    };

    opens.forEach((btn) => btn.addEventListener('click', open));
    if (closeBtn) closeBtn.addEventListener('click', close);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.hasAttribute('open')) close();
    });
  }

  document.addEventListener('DOMContentLoaded', () => initSizeChartModal(document));

  if (!customElements.get('tandf-gallery')) {
    customElements.define('tandf-gallery', TandfGallery);
  }
  if (!customElements.get('tandf-variant-picker')) {
    customElements.define('tandf-variant-picker', TandfVariantPicker);
  }
  if (!customElements.get('tandf-product-carousel')) {
    customElements.define('tandf-product-carousel', TandfProductCarousel);
  }
  if (!customElements.get('tandf-buy-form')) {
    customElements.define('tandf-buy-form', TandfBuyForm);
  }
})();
